import type { CollectionPoint } from '@akademi/site-builder-runtime';

/**
 * Opening-hours arithmetic for collection points, in the point's own time zone.
 *
 * <p>Everything is read in the store's zone, never the browser's: a shopper in another country
 * looking at a London counter needs London's "open today", and a closure is a calendar date where
 * the store is.
 */

const WEEKDAYS: Record<string, number> = { Mon: 1, Tue: 2, Wed: 3, Thu: 4, Fri: 5, Sat: 6, Sun: 7 };

export interface LocalNow {
  /** ISO weekday, 1 = Monday. */
  weekday: number;
  /** `YYYY-MM-DD`. */
  date: string;
  /** `HH:mm`, 24-hour. */
  time: string;
}

export function nowIn(timeZone: string, at: Date = new Date()): LocalNow {
  let parts: Intl.DateTimeFormatPart[];
  try {
    parts = new Intl.DateTimeFormat('en-GB', {
      timeZone,
      weekday: 'short',
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      hourCycle: 'h23',
    }).formatToParts(at);
  } catch {
    parts = new Intl.DateTimeFormat('en-GB', {
      timeZone: 'UTC',
      weekday: 'short',
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      hourCycle: 'h23',
    }).formatToParts(at);
  }
  const part = (type: string) => parts.find((p) => p.type === type)?.value ?? '';
  return {
    weekday: WEEKDAYS[part('weekday')] ?? 1,
    date: `${part('year')}-${part('month')}-${part('day')}`,
    time: `${part('hour')}:${part('minute')}`,
  };
}

/** The day's opening ranges, e.g. `09:00–13:00, 14:00–17:30`; empty when closed that day. */
export function hoursOn(point: CollectionPoint, weekday: number): string {
  return point.hours
    .filter((row) => row.weekday === weekday)
    .map((row) => `${row.opensAt}–${row.closesAt}`)
    .join(', ');
}

export function closedOn(point: CollectionPoint, date: string): boolean {
  return point.closures.some((closure) => closure.date === date);
}

/** Open at this moment, in the store's zone — weekly hours, less any closure today. */
export function isOpenNow(point: CollectionPoint, at: Date = new Date()): boolean {
  const now = nowIn(point.timeZone, at);
  if (closedOn(point, now.date)) return false;
  return point.hours.some(
    (row) => row.weekday === now.weekday && row.opensAt <= now.time && now.time < row.closesAt,
  );
}

/** Today's hours in the store's zone, or null when it is closed today. */
export function todaysHours(point: CollectionPoint, at: Date = new Date()): string | null {
  const now = nowIn(point.timeZone, at);
  if (closedOn(point, now.date)) return null;
  return hoursOn(point, now.weekday) || null;
}

/** One line of address, for a list. */
export function addressLine(point: CollectionPoint): string {
  const a = point.address;
  return [a.line1, a.line2, a.locality, a.postcode].filter((part) => part && part.trim()).join(', ');
}

/** A maps search a phone can hand to its own maps app; coordinates when the shop recorded them. */
export function directionsUrl(point: CollectionPoint): string {
  const a = point.address;
  const query =
    a.latitude && a.longitude
      ? `${a.latitude},${a.longitude}`
      : [point.name, a.line1, a.locality, a.postcode, a.countryCode].filter(Boolean).join(', ');
  return `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(query)}`;
}
